import {
  collection,
  doc,
  documentId,
  getDocs,
  onSnapshot,
  query,
  where
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions, ENV_SUFFIX } from '../firebase'

// The one place the web app resolves a user id it does not necessarily own.
//
// WHY THIS EXISTS. The users rule is "your own document, your own organisation,
// or an organisation you hold a dispatch binding to". Two real cases fall
// outside it and CANNOT be expressed as a rule at all: sharing a chat, and
// being on the same live incident after the binding that put you there was
// removed. A users/{id} read carries no chat id and no incident id, and rules
// cannot search for one — they can only get() a path already known. So the
// check runs server-side in getUserProfiles (functions/index.js), live against
// the chat and incident documents.
//
// WHAT IT BROKE BEFORE. Every cross-organisation responder rendered blank:
//
//   * `where(documentId(), 'in', ids)` is refused OUTRIGHT if a single id in the
//     batch is unreadable — rules reject queries, they do not filter them. One
//     stranger in a chat emptied the whole member list, not just their own row.
//   * onSnapshot on a denied document calls the error callback, and the three
//     listeners in the chat had no error callback at all, so it surfaced as an
//     uncaught SDK error and the tile simply never populated.
//
// WHAT COMES BACK from the fallback is a display projection and nothing else:
// id, displayName, profileUrl, userType, isOnline, dutyStatus, organization. No
// email, no location, no roleBindings, no MFA material — those belong to an
// organisation we hold no binding to. So a caller reading `firstName`,
// `lastName` or `email` off one of these gets undefined, by design; fall back
// to displayName rather than rendering an empty string.

const TTL_MS = 5 * 60 * 1000

// Short-lived on purpose, mirroring UserRepository._profileCache in the mobile
// app. A chat of fifty messages from one sender would otherwise be fifty calls,
// while a cache that never expired would bring back exactly the staleness the
// embedded chat copy already has — a rename or a new picture would not appear
// until the tab was reloaded.
const cache = new Map() // id -> { at, profile|null }
const inFlight = new Map() // id -> Promise<profile|null>

// `in` takes at most 30 values and getUserProfiles caps userIds at 30 as well,
// so one chunk maps to one call on either path.
const CHUNK = 30

const fresh = (entry) => entry && Date.now() - entry.at < TTL_MS

const remember = (id, profile) => {
  // Misses are cached too. Without that, a deleted user or one the server also
  // refuses is re-requested on every render for as long as the row is on screen.
  cache.set(id, { at: Date.now(), profile: profile || null })
  return profile || null
}

const chunk = (items) => {
  const out = []
  for (let i = 0; i < items.length; i += CHUNK) out.push(items.slice(i, i + CHUNK))
  return out
}

/** Ask the server for the ids Firestore would not answer for. */
const fetchFromFunction = async (ids) => {
  try {
    const res = await httpsCallable(functions, `getUserProfiles${ENV_SUFFIX}`)({
      userIds: ids
    })
    return (res?.data?.profiles || []).reduce(
      (acc, profile) => ({ ...acc, [profile.id]: profile }),
      {}
    )
  } catch (err) {
    // An id the server refuses is absent from `profiles`, not an error, so
    // landing here means the call itself failed — offline, or not signed in.
    console.error('getUserProfiles failed:', err)
    return {}
  }
}

/**
 * Resolve many user ids at once. Never throws; an id that cannot be resolved is
 * simply absent from the result, so one stranger does not fail the batch.
 *
 * @param {string[]} userIds
 * @returns {Promise<Object<string, object>>} id -> user, for the ids resolved
 */
export const fetchUserProfiles = async (userIds) => {
  const ids = [...new Set((userIds || []).map((id) => `${id}`.trim()).filter(Boolean))]
  if (!ids.length) return {}

  const found = {}
  const wanted = []
  for (const id of ids) {
    const entry = cache.get(id)
    if (fresh(entry)) {
      if (entry.profile) found[id] = entry.profile
    } else {
      wanted.push(id)
    }
  }
  if (!wanted.length) return found

  await Promise.all(
    chunk(wanted).map(async (group) => {
      let readable = {}
      try {
        const snap = await getDocs(
          query(collection(db, 'users'), where(documentId(), 'in', group))
        )
        snap.forEach((d) => {
          readable[d.id] = { id: d.id, ...d.data() }
        })
      } catch (err) {
        // Denied. The whole group failed, not just the unreadable member, so
        // every id in it goes to the server rather than only the missing ones.
        readable = {}
      }

      const missing = group.filter((id) => !readable[id])
      const viaFunction = missing.length ? await fetchFromFunction(missing) : {}

      for (const id of group) {
        const profile = remember(id, readable[id] || viaFunction[id])
        if (profile) found[id] = profile
      }
    })
  )

  return found
}

/**
 * Resolve one user id. Deduplicates concurrent callers: a message list renders
 * many tiles at once, all asking for the same sender before the first answer
 * lands.
 *
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
export const fetchUserProfile = async (userId) => {
  const id = `${userId || ''}`.trim()
  if (!id) return null

  const entry = cache.get(id)
  if (fresh(entry)) return entry.profile

  const pending = inFlight.get(id)
  if (pending) return pending

  const promise = fetchUserProfiles([id]).then((byId) => byId[id] || null)
  inFlight.set(id, promise)
  try {
    return await promise
  } finally {
    inFlight.delete(id)
  }
}

/**
 * Live user document, degrading to a one-shot profile when the rules refuse it.
 *
 * A callable cannot back a snapshot listener, so a denied user stops updating
 * and is resolved once instead. That is the right trade for what these
 * listeners actually drive — a name, a picture and an online dot for someone in
 * another organisation.
 *
 * @param {string} userId
 * @param {(user: object|null) => void} onUser
 * @returns {() => void} unsubscribe
 */
export const watchUserProfile = (userId, onUser) => {
  const id = `${userId || ''}`.trim()
  if (!id) return () => {}

  let live = true
  const unsubscribe = onSnapshot(
    doc(db, 'users', id),
    (snap) => {
      if (!live) return
      onUser(snap.exists() ? { id: snap.id, ...snap.data() } : null)
    },
    () => {
      // Without this callback a denied document surfaced as an uncaught error
      // in the SDK and the caller was never told anything at all.
      if (!live) return
      fetchUserProfile(id).then((profile) => {
        if (live) onUser(profile)
      })
    }
  )

  return () => {
    live = false
    unsubscribe()
  }
}
